import { cur } from '@/lib/currency';
import { useEffect, useState, useMemo } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { ExportButtons } from '@/components/ExportButtons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import {
  Building2, TrendingUp, TrendingDown, DollarSign, Calendar, ArrowUpRight,
  ArrowDownRight, Wallet, Banknote, CreditCard, Activity, PieChart, Bell, Settings, AlertTriangle, Receipt, GitCompare
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import BranchLink from '@/components/BranchLink';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import { BranchMultiSelect } from '@/components/BranchMultiSelect';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { useBranchStore } from '@/stores';
import { Link } from 'react-router-dom';

export default function BranchCostAnalyzerPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(() => {
    const now = new Date();
    return {
      start_date: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`,
      end_date: format(now, 'yyyy-MM-dd'),
    };
  });
  const [minCredit, setMinCredit] = useState('');
  const [branchSel, setBranchSel] = useState([]);
  const { branches: allBranchList, fetchBranches } = useBranchStore();
  useEffect(() => { fetchBranches(); }, []);
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  const [thresholds, setThresholds] = useState([]);
  const [alertResults, setAlertResults] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.get('/accounting/branch-cost-analysis', {
          params: {
            start_date: range.start_date || undefined,
            end_date: range.end_date || undefined,
            min_credit: minCredit !== '' ? minCredit : undefined,
          },
        });
        setData(res.data);
      } catch { toast.error('Failed to load branch analysis'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [range, minCredit]);

  const fetchThresholds = async () => {
    try { const res = await api.get('/accounting/branch-cost-thresholds'); setThresholds(res.data); } catch {}
  };
  useEffect(() => { fetchThresholds(); }, []);

  const saveThreshold = async (branchId, branchName, dailyLimit, monthlyLimit, enabled) => {
    try {
      await api.post('/accounting/branch-cost-thresholds', { branch_id: branchId, branch_name: branchName, daily_limit: dailyLimit, monthly_limit: monthlyLimit, enabled });
      toast.success(`Alert saved for ${branchName}`);
      fetchThresholds();
    } catch { toast.error('Failed to save threshold'); }
  };

  const checkAlerts = async () => {
    try {
      const res = await api.post('/accounting/branch-cost-check-alerts');
      setAlertResults(res.data);
      if (res.data.alerts?.length > 0) toast.warning(`${res.data.alerts.length} cost alert(s) triggered!`);
      else toast.success('All branches within limits');
    } catch { toast.error('Failed to check alerts'); }
  };

  const allBranches = data?.branches || [];
  // Multi-branch client-side filter — [] means all.
  const shownBranches = branchSel.length ? allBranches.filter((b) => branchSel.includes(b.branch_id)) : allBranches;
  const totalRevenue = shownBranches.reduce((s, b) => s + b.revenue.total, 0);
  const totalCost = shownBranches.reduce((s, b) => s + b.total_cost, 0);
  const totalProfit = shownBranches.reduce((s, b) => s + b.profit, 0);
  const totalCashRevenue = shownBranches.reduce((s, b) => s + b.revenue.cash, 0);
  const totalBankRevenue = shownBranches.reduce((s, b) => s + b.revenue.bank, 0);
  const totalOnlineRevenue = shownBranches.reduce((s, b) => s + b.revenue.online, 0);
  const totalCreditRevenue = shownBranches.reduce((s, b) => s + b.revenue.credit, 0);
  const totalCashExpenses = shownBranches.reduce((s, b) => s + b.expenses.cash, 0);
  const totalBankExpenses = shownBranches.reduce((s, b) => s + b.expenses.bank, 0);
  const totalSalary = shownBranches.reduce((s, b) => s + b.expenses.salary, 0);
  // Company-wide actuals — summed from data already returned per branch (no new queries).
  const totalCashInHand = shownBranches.reduce((s, b) => s + (b.cash_position?.cash_in_hand || 0), 0);
  const totalBankBalance = shownBranches.reduce((s, b) => s + (b.cash_position?.bank_in_hand || 0), 0);

  // Aggregate expense categories across all branches
  const allCategories = {};
  shownBranches.forEach(b => {
    Object.entries(b.expenses.categories || {}).forEach(([k, v]) => {
      allCategories[k] = (allCategories[k] || 0) + v;
    });
  });
  const sortedCategories = Object.entries(allCategories).sort((a, b) => b[1] - a[1]);

  const profitableBranches = [...shownBranches].sort((a, b) => b.profit - a.profit);
  const costliestBranches = [...shownBranches].sort((a, b) => b.cost_per_day - a.cost_per_day);

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-7xl mx-auto" data-testid="branch-cost-analyzer">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-teal-200/50">
              <Activity size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">Branch Cost Analyzer</h1>
            <ExportButtons branchIds={branchSel} dataType="branch_cost" label="Branch Cost" startDate={data?.period?.start || range.start_date} endDate={data?.period?.end || range.end_date} />
              <p className="text-sm text-muted-foreground mt-0.5">Revenue, costs & profitability per branch</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowAlertSettings(true)} className="rounded-xl gap-1.5" data-testid="alert-settings-btn">
              <Bell size={14} /> Cost Alerts
            </Button>
            <Button variant="outline" size="sm" onClick={checkAlerts} className="rounded-xl gap-1.5" data-testid="check-alerts-btn">
              <AlertTriangle size={14} /> Check Now
            </Button>
            <Input
              type="number"
              min="0"
              placeholder="Min Credit"
              title="Only show branches with at least this much credit revenue in the period"
              value={minCredit}
              onChange={(e) => setMinCredit(e.target.value)}
              className="w-28 rounded-xl h-10"
              data-testid="min-credit-filter"
            />
            <BranchMultiSelect value={branchSel} onChange={setBranchSel} branches={allBranchList} testid="cost-branch-multiselect" />
            <DateRangeFilter value={range} onChange={setRange} className="rounded-xl" testid="period-filter" />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Total Network Summary — company-wide actuals summed across all branches */}
            <Card className="border-0 rounded-2xl overflow-hidden shadow-lg bg-gradient-to-br from-stone-800 to-stone-900 text-white" data-testid="network-summary-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 size={16} className="text-teal-300" />
                  <span className="text-xs font-bold uppercase tracking-wider text-white/70">Total Network Summary</span>
                  <span className="text-[10px] text-white/40">· {allBranches.length} branches</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="min-w-0" data-testid="network-total-revenue">
                    <p className="text-[10px] uppercase tracking-wider text-white/50 font-semibold truncate">Total Revenue</p>
                    <p className="text-lg sm:text-xl font-bold text-emerald-300 truncate tabular-nums">{cur()} {totalRevenue.toLocaleString()}</p>
                  </div>
                  <div className="min-w-0" data-testid="network-total-cost">
                    <p className="text-[10px] uppercase tracking-wider text-white/50 font-semibold truncate">Total Cost</p>
                    <p className="text-lg sm:text-xl font-bold text-rose-300 truncate tabular-nums">{cur()} {totalCost.toLocaleString()}</p>
                  </div>
                  <div className="min-w-0" data-testid="network-total-profit">
                    <p className="text-[10px] uppercase tracking-wider text-white/50 font-semibold truncate">Profit</p>
                    <p className={`text-lg sm:text-xl font-bold truncate tabular-nums ${totalProfit >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{cur()} {totalProfit.toLocaleString()}</p>
                  </div>
                  <div className="min-w-0" data-testid="network-cash-in-hand">
                    <p className="text-[10px] uppercase tracking-wider text-white/50 font-semibold truncate flex items-center gap-1"><Wallet size={11} className="shrink-0" /> Cash in Hand</p>
                    <p className={`text-lg sm:text-xl font-bold truncate tabular-nums ${totalCashInHand >= 0 ? 'text-white' : 'text-rose-300'}`}>{cur()} {totalCashInHand.toLocaleString()}</p>
                  </div>
                  <div className="min-w-0" data-testid="network-bank-balance">
                    <p className="text-[10px] uppercase tracking-wider text-white/50 font-semibold truncate flex items-center gap-1"><Banknote size={11} className="shrink-0" /> Bank Balance</p>
                    <p className={`text-lg sm:text-xl font-bold truncate tabular-nums ${totalBankBalance >= 0 ? 'text-white' : 'text-rose-300'}`}>{cur()} {totalBankBalance.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards Row 1: Revenue */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <SummaryCard icon={DollarSign} label="Total Revenue" value={totalRevenue} color="emerald" />
              <SummaryCard icon={Wallet} label="Cash Sales" value={totalCashRevenue} color="green" />
              <SummaryCard icon={Banknote} label="Bank Sales" value={totalBankRevenue} color="blue" />
              <SummaryCard icon={CreditCard} label="Online Sales" value={totalOnlineRevenue} color="violet" />
              <SummaryCard icon={CreditCard} label="Credit Sales" value={totalCreditRevenue} color="amber" />
            </div>

            {/* Summary Cards Row 2: Costs & Profit */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <SummaryCard icon={TrendingDown} label="Total Costs" value={totalCost} color="red" />
              <SummaryCard icon={Wallet} label="Cash Expenses" value={totalCashExpenses} color="orange" />
              <SummaryCard icon={Banknote} label="Bank Expenses" value={totalBankExpenses} color="sky" />
              <SummaryCard icon={DollarSign} label="Salaries" value={totalSalary} color="purple" />
              <SummaryCard icon={TrendingUp} label="Net Profit" value={totalProfit} color={totalProfit >= 0 ? 'emerald' : 'red'} bold />
            </div>

            {/* Expense Categories */}
            {sortedCategories.length > 0 && (
              <Card className="border-border/60 rounded-xl">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2"><PieChart size={14} /> Expense Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {sortedCategories.map(([cat, amt]) => (
                      <div key={cat} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-muted/50 border border-border">
                        <span className="text-xs font-medium text-muted-foreground capitalize">{cat}</span>
                        <span className="text-xs font-bold text-foreground">{cur()} {amt.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Branch Cards */}
            <div>
              <h2 className="text-lg font-bold font-outfit text-foreground mb-3">Branch Breakdown</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {profitableBranches.map((branch, idx) => (
                  <Card key={branch.branch_id} className={`border-border/60 rounded-xl overflow-hidden ${idx === 0 ? 'ring-2 ring-emerald-200' : ''}`} data-testid={`branch-card-${branch.branch_id}`}>
                    <CardHeader className="pb-2 bg-muted/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-stone-700 to-stone-900 flex items-center justify-center text-white font-bold text-xs">
                            {branch.branch_name[0]}
                          </div>
                          <div>
                            <CardTitle className="text-sm font-bold">
                              <BranchLink branchId={branch.branch_id} name={branch.branch_name} />
                            </CardTitle>
                            <p className="text-[10px] text-muted-foreground">{branch.period_days} days</p>
                          </div>
                        </div>
                        <Badge className={`text-xs ${branch.profit >= 0 ? 'bg-success/10 text-success border-success/30' : 'bg-error/10 text-error border-error/30'}`}>
                          {branch.profit >= 0 ? <ArrowUpRight size={12} className="mr-0.5" /> : <ArrowDownRight size={12} className="mr-0.5" />}
                          {branch.profit_margin}% margin
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-3 space-y-3">
                      {/* Revenue/Cost/Profit row */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        <div className="text-center p-2 rounded-lg bg-success/10">
                          <p className="text-[10px] text-muted-foreground font-semibold uppercase">Revenue</p>
                          <p className="text-sm font-bold text-success">{cur()} {branch.revenue.total.toLocaleString()}</p>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-error/10">
                          <p className="text-[10px] text-muted-foreground font-semibold uppercase">Total Cost</p>
                          <p className="text-sm font-bold text-error">{cur()} {branch.total_cost.toLocaleString()}</p>
                          <p className="text-[9px] text-muted-foreground mt-0.5" data-testid={`cost-split-${branch.branch_id}`}>COGS: {cur()} {(branch.cogs || 0).toLocaleString()} | Ops: {cur()} {(branch.operating_expenses || 0).toLocaleString()}</p>
                        </div>
                        <div className={`text-center p-2 rounded-lg ${branch.profit >= 0 ? 'bg-success/10' : 'bg-error/10'}`}>
                          <p className="text-[10px] text-muted-foreground font-semibold uppercase">Profit</p>
                          <p className={`text-sm font-bold ${branch.profit >= 0 ? 'text-success' : 'text-error'}`}>{cur()} {branch.profit.toLocaleString()}</p>
                        </div>
                      </div>

                      {/* Cost per day/month */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center gap-2 p-2 rounded-lg bg-warning/10 border border-warning/30">
                          <Calendar size={14} className="text-warning" />
                          <div>
                            <p className="text-[10px] text-muted-foreground font-semibold">Cost / Day</p>
                            <p className="text-sm font-bold text-warning">{cur()} {branch.cost_per_day.toLocaleString()}</p>
                          </div>
                        </div>
                        {branch.period_days >= 3 && (
                        <div className="flex items-center gap-2 p-2 rounded-lg bg-warning/10 border border-warning/30">
                          <Calendar size={14} className="text-warning" />
                          <div>
                            <p className="text-[10px] text-muted-foreground font-semibold">Monthly Forecast</p>
                            <p className="text-sm font-bold text-warning">{cur()} {branch.cost_per_month.toLocaleString()}</p>
                            <p className="text-[9px] text-muted-foreground" data-testid={`forecast-basis-${branch.branch_id}`}>Based on {branch.period_days} of {branch.days_in_month || branch.period_days} days elapsed</p>
                          </div>
                        </div>
                        )}
                      </div>

                      {/* Revenue breakdown */}
                      <div className="flex gap-1.5 text-[10px]">
                        {branch.revenue.cash > 0 && <span className="px-2 py-0.5 rounded-full bg-success/20 text-success font-semibold">Cash: {branch.revenue.cash.toLocaleString()}</span>}
                        {branch.revenue.bank > 0 && <span className="px-2 py-0.5 rounded-full bg-info/20 text-info font-semibold">Bank: {branch.revenue.bank.toLocaleString()}</span>}
                        {branch.revenue.online > 0 && <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-semibold">Online: {branch.revenue.online.toLocaleString()}</span>}
                        {branch.revenue.credit > 0 && <span className="px-2 py-0.5 rounded-full bg-warning/20 text-warning font-semibold">Credit: {branch.revenue.credit.toLocaleString()}</span>}
                      </div>

                      {/* Expense categories — each tile shows a Cash-vs-Bank split bar (iter925) */}
                      {Object.keys(branch.expenses.categories || {}).length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-[10px]">
                          {Object.entries(branch.expenses.categories).slice(0, 6).map(([cat, amt]) => {
                            const cm = (branch.expenses.categories_mode || {})[cat] || { cash: 0, bank: 0 };
                            const known = (cm.cash || 0) + (cm.bank || 0);
                            const cashPct = known > 0 ? (cm.cash / known) * 100 : 0;
                            const bankPct = known > 0 ? (cm.bank / known) * 100 : 0;
                            return (
                              <div key={cat} className="px-2 py-1 rounded-lg bg-muted/60" data-testid={`expense-cat-${branch.branch_id}-${cat}`}>
                                <div className="flex items-center justify-between gap-1">
                                  <span className="font-medium text-muted-foreground capitalize truncate">{cat}</span>
                                  <span className="font-semibold tabular-nums">{amt.toLocaleString()}</span>
                                </div>
                                <div className="mt-1 h-1.5 rounded-full bg-border overflow-hidden flex" title={`Cash ${cm.cash.toLocaleString()} · Bank ${cm.bank.toLocaleString()}`}>
                                  <div className="h-full bg-success" style={{ width: `${cashPct}%` }} data-testid={`cat-bar-cash-${branch.branch_id}-${cat}`} />
                                  <div className="h-full bg-info" style={{ width: `${bankPct}%` }} data-testid={`cat-bar-bank-${branch.branch_id}-${cat}`} />
                                </div>
                                <div className="flex items-center justify-between mt-0.5 text-[8px] text-muted-foreground">
                                  <span className="text-success font-semibold">Cash {Math.round(cashPct)}%</span>
                                  <span className="text-info font-semibold">Bank {Math.round(bankPct)}%</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Fixed vs Variable */}
                      {branch.total_cost > 0 && (
                        <div className="flex gap-2">
                          <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                            <div className="h-full bg-blue-400 rounded-full" style={{ width: `${branch.expenses.fixed / branch.total_cost * 100}%` }} />
                          </div>
                          <span className="text-[10px] text-muted-foreground">Fixed: {Math.round(branch.expenses.fixed / branch.total_cost * 100)}%</span>
                        </div>
                      )}

                      {/* Remaining (Owed) — clickable → unpaid bills, reuses bills.balance_due aggregation */}
                      {(branch.expenses?.remaining_owed || 0) > 0 && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Link to="/bills" className="flex items-center justify-between p-2 rounded-lg bg-amber-50 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-colors dark:bg-amber-950/20 dark:border-amber-900 dark:hover:bg-amber-950/40 cursor-pointer" data-testid={`branch-owed-${branch.branch_id}`}>
                                <span className="text-[10px] font-semibold uppercase text-amber-700 dark:text-amber-400 flex items-center gap-1 min-w-0 truncate"><Receipt size={12} className="shrink-0" /> Remaining (Owed)</span>
                                <span className="text-sm font-bold text-amber-700 dark:text-amber-400 shrink-0 underline decoration-dotted underline-offset-2">{cur()} {branch.expenses.remaining_owed.toLocaleString()}</span>
                              </Link>
                            </TooltipTrigger>
                            <TooltipContent>Open Bills to view & settle this branch's unpaid balance</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}

                      {/* Inter-Branch Actuals — clickable → inter-branch ledger, reuses inter_branch_net_outstanding */}
                      {typeof branch.inter_branch_net_outstanding === 'number' && branch.inter_branch_net_outstanding !== 0 && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Link to="/inter-branch-ledger" className="flex items-center justify-between p-2 rounded-lg bg-violet-50 border border-violet-200 hover:bg-violet-100 hover:border-violet-300 transition-colors dark:bg-violet-950/20 dark:border-violet-900 dark:hover:bg-violet-950/40 cursor-pointer" data-testid={`branch-interbranch-${branch.branch_id}`}>
                                <span className="text-[10px] font-semibold uppercase text-violet-700 dark:text-violet-400 flex items-center gap-1 min-w-0 truncate"><GitCompare size={12} className="shrink-0" /> Inter-Branch Net</span>
                                <span className={`text-sm font-bold shrink-0 underline decoration-dotted underline-offset-2 ${branch.inter_branch_net_outstanding >= 0 ? 'text-success' : 'text-error'}`}>
                                  {branch.inter_branch_net_outstanding >= 0 ? '+' : ''}{cur()} {branch.inter_branch_net_outstanding.toLocaleString()}
                                </span>
                              </Link>
                            </TooltipTrigger>
                            <TooltipContent>{branch.inter_branch_net_outstanding >= 0 ? 'This branch is owed by others' : 'This branch owes others'} — open the Inter-Branch Ledger to settle</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}

                      {/* Final Cash Position — reuses per-branch cash_position (cash/bank balances) */}
                      {branch.cash_position && (
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/60" data-testid={`branch-cash-position-${branch.branch_id}`}>
                          <div className="flex items-center gap-2 p-2 rounded-lg bg-success/10">
                            <Wallet size={14} className="text-success shrink-0" />
                            <div className="min-w-0">
                              <p className="text-[10px] text-muted-foreground font-semibold truncate">Cash in Hand</p>
                              <p className="text-sm font-bold text-success truncate">{cur()} {(branch.cash_position.cash_in_hand || 0).toLocaleString()}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 p-2 rounded-lg bg-info/10">
                            <Banknote size={14} className="text-info shrink-0" />
                            <div className="min-w-0">
                              <p className="text-[10px] text-muted-foreground font-semibold truncate">Bank Balance</p>
                              <p className="text-sm font-bold text-info truncate">{cur()} {(branch.cash_position.bank_in_hand || 0).toLocaleString()}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Alert Results Banner */}
        {alertResults && alertResults.alerts?.length > 0 && (
          <Card className="border-error/30 bg-error/10 rounded-xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={16} className="text-error" />
                <span className="text-sm font-bold text-error">{alertResults.alerts.length} Cost Alert(s)</span>
              </div>
              <div className="space-y-1">
                {alertResults.alerts.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-error" data-testid={`alert-result-${i}`}>
                    <Badge className={`text-[10px] ${a.type === 'daily' ? 'bg-orange-100 text-orange-700' : 'bg-error/20 text-error'}`}>{a.type}</Badge>
                    <span>{a.message}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cost Alert Settings Dialog */}
        <Dialog open={showAlertSettings} onOpenChange={setShowAlertSettings}>
          <DialogContent className="max-w-xl rounded-2xl" data-testid="alert-settings-dialog">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold font-outfit flex items-center gap-2"><Bell size={18} /> Branch Cost Alert Settings</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground -mt-2">Set daily and monthly cost limits per branch. Notifications will be created when limits are exceeded.</p>
            <div className="space-y-3 mt-2 max-h-[400px] overflow-y-auto">
              {allBranches.map(branch => {
                const thr = thresholds.find(t => t.branch_id === branch.branch_id) || {};
                return (
                  <ThresholdRow
                    key={branch.branch_id}
                    branch={branch}
                    threshold={thr}
                    onSave={saveThreshold}
                  />
                );
              })}
              {allBranches.length === 0 && <p className="text-center text-muted-foreground text-sm py-6">No branches found. Load data first.</p>}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function SummaryCard({ icon: Icon, label, value, color, bold }) {
  const colorMap = {
    emerald: 'bg-success/10 text-success', green: 'bg-success/10 text-success',
    blue: 'bg-info/10 text-info', violet: 'bg-violet-50 text-violet-600',
    amber: 'bg-warning/10 text-warning', red: 'bg-error/10 text-error',
    orange: 'bg-orange-50 text-orange-600', sky: 'bg-info/10 text-info',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <Card className="border-border/60">
      <CardContent className="p-3 flex items-center gap-2.5">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${colorMap[color] || 'bg-muted/50 text-muted-foreground'}`}>
          <Icon size={16} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground truncate">{label}</p>
          <p className={`text-base font-bold ${bold ? (value >= 0 ? 'text-success' : 'text-error') : 'text-foreground'} truncate`}>
            {cur()} {(value || 0).toLocaleString()}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function ThresholdRow({ branch, threshold, onSave }) {
  const [daily, setDaily] = useState(threshold.daily_limit || '');
  const [monthly, setMonthly] = useState(threshold.monthly_limit || '');
  const [enabled, setEnabled] = useState(threshold.enabled !== false);

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border border-border bg-muted/50" data-testid={`threshold-${branch.branch_id}`}>
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-stone-700 to-stone-900 flex items-center justify-center text-white font-bold text-xs shrink-0">
        {branch.branch_name[0]}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate">{branch.branch_name}</p>
        <p className="text-[10px] text-muted-foreground">Current: {cur()} {branch.cost_per_day}/day, {cur()} {branch.cost_per_month}/month</p>
      </div>
      <div className="flex items-center gap-2">
        <div>
          <label className="text-[10px] text-muted-foreground block">Daily Limit</label>
          <Input type="number" value={daily} onChange={e => setDaily(e.target.value)} className="w-24 h-8 text-xs rounded-lg" placeholder="0" />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground block">Monthly Limit</label>
          <Input type="number" value={monthly} onChange={e => setMonthly(e.target.value)} className="w-24 h-8 text-xs rounded-lg" placeholder="0" />
        </div>
        <div className="flex flex-col items-center gap-0.5">
          <label className="text-[10px] text-muted-foreground">On</label>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </div>
        <Button size="sm" className="h-8 rounded-lg text-xs" onClick={() => onSave(branch.branch_id, branch.branch_name, parseFloat(daily) || 0, parseFloat(monthly) || 0, enabled)} data-testid={`save-threshold-${branch.branch_id}`}>
          Save
        </Button>
      </div>
    </div>
  );
}
